import { type ChannelPreviewUIComponentProps } from 'stream-chat-react'
import { View, Text, Badge, AvatarInfo, Icon } from '@/components'
import { AppIcon, createStyles } from '@codeleap/styles'
import { textVariant, Chat } from '@/config'
import { useMemo, useCallback } from 'react'
import { Profile } from '@packages/types'
import { useChatPreviewAvatar } from '@/hooks'
import {
  ChatLocalMesage,
  chatPreviewIcons,
  chatPreviewStatus,
  chatPreviewText,
  formatChatCreatedAt,
  getChatUnreadCount,
  getChatUnreadMentions,
  isChatMarkedAsUnread,
  shouldChatRerender,
} from '@packages/chat'
import { ChatPreviewActionMenu, ChatPreviewMention } from './components'
import React, { useState, memo } from 'react'
import { ChatMuteActionModal } from '../ChatMuteActionModal'
import { appStatusStore } from '@/stores'
import { useIsChannelMuted } from 'stream-chat-react'
import { Getstream } from '@/services'
import { Medias } from '@/config/packages'

type ChatPreviewProps = ChannelPreviewUIComponentProps & {
  onSelect: (channel: any) => void
  selected: boolean
  profile: Profile
  filters: Record<string, any>
}

const getMentionPreference = (channel: any, userId?: string) => {
  const cid = channel?.cid
  const storageKey = cid && userId ? `mention_notifications:${cid}:${userId}` : ''
  const stored = typeof window !== 'undefined' && storageKey ? window.localStorage.getItem(storageKey) : null
  const channelKey = userId ? `mention_notifications_${userId}` : ''

  return stored || (channel?.data as any)?.[channelKey] || 'all'
}

const ChatPreviewComponent = (props: ChatPreviewProps) => {
  const {
    channel,
    setActiveChannel,
    onSelect,
    displayTitle,
    unread,
    lastMessage,
    selected,
    messageDeliveryStatus,
    profile,
    filters,
  } = props
  const { refreshList } = Getstream.Chat.useChannelListRefresh(filters)
  const isMuted = useIsChannelMuted(channel)?.muted
  const [muteModalVisible, setMuteModalVisible] = useState(false)

  // Get pinned status directly from channel state (avoids context subscription)
  const isPinned = !!channel.state.membership?.pinned_at

  const truncatedTitle = useMemo(() => {
    if (!displayTitle) return ''
    return displayTitle.length > 20 ? `${displayTitle.substring(0, 20)}...` : displayTitle
  }, [displayTitle])

  const handlePress = useCallback(() => {
    if (selected) return
    setActiveChannel?.(channel)
    onSelect?.(channel?.data)
  }, [selected, setActiveChannel, channel, onSelect])

  const handleMarkAsUnread = useCallback(async () => {
    await Chat.Services.toggleReadChat(channel)
  }, [channel])

  const handlePin = useCallback(async () => {
    appStatusStore.set('loading')
    await Chat.Services.toggleChatPin(channel, isPinned, {
      // Re-sort the list after unpinning so the channel moves to its correct position
      onUnpinSuccess: () => refreshList(),
    })
    appStatusStore.set('idle')
  }, [channel, isPinned, refreshList])

  const handleMuteUnmute = useCallback(async (e?: React.MouseEvent) => {
    e?.stopPropagation()

    if (isMuted) {
      await Chat.Services.toggleMuteChat(channel)
    } else {
      setMuteModalVisible(true)
    }
  }, [channel, isMuted])

  const handleDeleteChat = useCallback((e?: React.MouseEvent) => {
    e?.stopPropagation?.()
    if (!channel || !displayTitle) return

    Chat.Services.deleteChat(channel, displayTitle)
  }, [channel, displayTitle])

  const toggleMuteModal = useCallback(() => setMuteModalVisible(false), [])

  const messageUser = lastMessage?.user
  const isMyMessage = messageUser?.id === profile?.id
  const attachment = lastMessage?.attachments?.[0]
  const hasText = !!lastMessage?.text?.trim()
  const hasLastMessage = !!lastMessage?.created_at
  const isRead = messageDeliveryStatus === 'read'
  const previewStatus = useMemo(() => {
    return chatPreviewStatus({
      isMyMessage,
      deliveryStatus: messageDeliveryStatus,
      messageStatus: lastMessage?.status,
    })
  }, [isMyMessage, lastMessage?.status, messageDeliveryStatus])
  const previewAvatar = useChatPreviewAvatar({ channel })
  const { src: resolvedPreviewAvatar } = Medias.useResolvedUrl(previewAvatar)
  const { isAdmin, isGroup } = Getstream.Chat.useMyMembership(channel)

  const previewText = useMemo(() => {
    if (!lastMessage) return ''

    return chatPreviewText({
      message: lastMessage as ChatLocalMesage,
      channel,
      isMyMessage,
      userName: messageUser?.name,
    })
  }, [lastMessage?.id, lastMessage?.text, attachment, hasText, messageUser, isMyMessage])

  const descriptionIcons = useMemo(() => {
    const icons = lastMessage ? chatPreviewIcons({ isMyMessage, previewStatus, message: lastMessage as ChatLocalMesage }) : []
    const iconNames = icons?.map(icon => icon.name as AppIcon) || []

    return iconNames
  }, [isMyMessage, lastMessage, previewStatus])

  const resolvedUnread = useMemo(() => {
    const channelUnread = getChatUnreadCount(channel)
    const resolvedUnread = Math.max(unread, channelUnread)
    return resolvedUnread
  }, [unread, channel, channel?.state?.unreadCount])

  const previewCreatedAt = lastMessage?.created_at

  // Pre-computed styles to avoid Emotion recalculation
  const avatarInfoStyle = useMemo(() => [
    'flex',
    'fullWidth',
    'x-large',
    'chatPreview',
    { descriptionIcon: isRead ? styles.descriptionIconRead : styles.descriptionIconUnread },
  ] as const, [isRead])

  return (
    <View
      onClick={handlePress}
      style={[styles.wrapper, selected && styles['wrapper:selected']]}
      className='chat-preview-card'
    >
      <AvatarInfo
        title={truncatedTitle}
        avatar={resolvedPreviewAvatar}
        description={previewText}
        descriptionIcons={descriptionIcons}
        style={avatarInfoStyle}
      />

      <View style={['column', 'gap:0.25', 'alignSelfStart', 'justifyCenter']}>
        {(hasLastMessage) && (
          <View style={styles.metadataWrapper}>
            {hasLastMessage && (
              <Text style={['c2', styles.date]} text={formatChatCreatedAt(previewCreatedAt)} />
            )}
          </View>
        )}

        <View style={[styles.pinWrapper]}>
          <Pin isPinned={isPinned} />
          {isMuted && (
            <Icon name='bell-off' style={styles.mutedIcon} />
          )}
          <UnreadBadge unread={resolvedUnread} channel={channel} currentUserId={profile?.id} />
        </View>

        <View
          style={styles.actionMenu}
          onClick={(e: React.MouseEvent) => e.stopPropagation()}
          className='chat-preview-action-menu-container'
        >
          <ChatPreviewActionMenu
            channel={channel}
            onMarkAsUnread={handleMarkAsUnread}
            onPin={handlePin}
            onMuteUnmute={handleMuteUnmute}
            onDeleteChat={handleDeleteChat}
            isAdmin={isAdmin}
            isGroup={isGroup}
            isPinned={isPinned}
          />
        </View>
      </View>

      {muteModalVisible && (
        <ChatMuteActionModal
          visible={muteModalVisible}
          channel={channel}
          toggle={toggleMuteModal}
        />
      )}
    </View>
  )
}

const areEqual = (prev: ChatPreviewProps, next: ChatPreviewProps) => {
  if (prev.channel?.id !== next.channel?.id) return false
  if (prev.displayTitle !== next.displayTitle) return false
  if (prev.displayImage !== next.displayImage) return false
  if (prev.unread !== next.unread) return false
  if (prev.selected !== next.selected) return false
  if (prev.messageDeliveryStatus !== next.messageDeliveryStatus) return false
  if (prev.profile !== next.profile) return false

  const prevMsg: any = (prev.lastMessage as any)?.messageObject || prev.lastMessage
  const nextMsg: any = (next.lastMessage as any)?.messageObject || next.lastMessage

  if (prevMsg?.id !== nextMsg?.id) return false
  if (prevMsg?.text !== nextMsg?.text) return false
  if (prevMsg?.updated_at !== nextMsg?.updated_at) return false
  if (prevMsg?.deleted_at !== nextMsg?.deleted_at) return false
  if (prevMsg?.created_at !== nextMsg?.created_at) return false

  const shouldRerender = shouldChatRerender(prev.channel?.id)
  if (shouldRerender) { return false }

  return true
}

const MemoizedChatPreview = memo(ChatPreviewComponent, areEqual)

export const ChatPreview = (props) => {
  const channelId = props?.channel?.id
  const isSelected = Chat.Store.state.use(state => state.channel?.id === channelId)
  Chat.Store.useShouldChatRerender(props.channel?.id || '')

  return (
    <MemoizedChatPreview
      {...props}
      selected={isSelected}
    />
  )
}

const Pin = memo(({ isPinned }: { isPinned: boolean }) => {
  if (!isPinned) return null

  return (
    <Icon name='pin-02-filled' style={styles.pin} />
  )
})

const UnreadBadge = ({ unread, channel, currentUserId }: { unread: number; channel: any; currentUserId?: string }) => {
  const isMarkedAsUnread = isChatMarkedAsUnread(channel)
  const unreadMentions = getChatUnreadMentions(channel)

  const mentionPreference = getMentionPreference(channel, currentUserId)
  const shouldHideUnread = mentionPreference === 'mentions' && unreadMentions === 0 && !isMarkedAsUnread
  const effectiveUnread = shouldHideUnread ? 0 : unread
  const badgeValue = mentionPreference === 'mentions' ? unreadMentions : effectiveUnread

  if (effectiveUnread > 0) {
    return (
      <View style={styles.badgeWrapper}>
        <ChatPreviewMention unreadMentions={unreadMentions} />
        <Badge
          style={['relative', 'highlighted', 'alignSelfEnd']}
          badge={badgeValue}
          renderBadgeContent={({ count }) => (
            <Text style={styles.badgeTxt} text={String(count)} />
          )}
        />
      </View>
    )
  }

  if (isMarkedAsUnread) return (<View style={styles.unreadIndicator} />)
  return null
}

const styles = createStyles(theme => ({
  wrapper: {
    position: 'relative',
    display: 'flex',
    width: '100%',
    maxWidth: '100%',
    minHeight: theme.size.large,
    height: theme.size['4xl'],
    backgroundColor: theme.baseColors.neutralSolid100,
    ...theme.border({ color: theme.colors.stroke, directions: ['bottom'], width: 1 }),
    ...theme.presets.row,
    ...theme.spacing.py(1.5),
    ...theme.spacing.px(3),
    ...theme.spacing.gap(1),
    ...theme.presets.alignCenter,
    ...theme.presets.justifyStart,
    overflow: 'hidden',
    cursor: 'pointer',
  },
  'wrapper:selected': {
    backgroundColor: theme.colors.rowBgHover,
  },
  date: {
    color: theme.baseColors.neutralBlackTransparent700,
  },
  badgeTxt: {
    ...textVariant('c3'),
    ...theme.presets.textCenter,
    color: theme.baseColors.onPrimarySolid500Heading,
  },
  unreadIndicator: {
    ...theme.sized(2),
    backgroundColor: theme.baseColors.primarySolid500,
    borderRadius: theme.radius.infinite,
  },
  mutedIcon: {
    alignSelf: 'flex-end',
    color: theme.baseColors.neutralSolid500,
    size: theme.size.regular,
  },
  metadataWrapper: {
    ...theme.presets.row,
    ...theme.presets.alignCenter,
    gap: theme.spacing.value(0.5),
    alignSelf: 'flex-end',
  },
  actionMenu: {
    alignSelf: 'flex-end',
    position: 'absolute',
    top: '70%',
    transform: 'translateY(-50%)',
    zIndex: 1001,
    transition: 'opacity 0.2s ease, visibility 0.2s ease',
  },
  pin: {
    ...theme.sized(1.5),
    color: theme.baseColors.neutralSolid500,
    rotate: '90deg',
  },
  pinWrapper: {
    width: 'auto',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: theme.spacing.value(0.5),
  },
  badgeWrapper: {
    ...theme.presets.row,
    ...theme.presets.alignCenter,
    gap: theme.spacing.value(0.5),
  },
  mentionIndicator: {
    fontSize: 16,
    fontWeight: '600',
    color: theme.baseColors.primarySolid500,
    lineHeight: '20px',
  },
  threadBadge: {
    ...theme.presets.row,
    ...theme.presets.alignCenter,
    alignSelf: 'flex-end',
    gap: theme.spacing.value(0.5),
    backgroundColor: theme.baseColors.primarySolid50,
    borderRadius: theme.radius.small,
    padding: '2px 6px',
  },
  threadBadgeIcon: {
    ...theme.sized(1),
    color: theme.baseColors.primarySolid500,
  },
  threadBadgeText: {
    ...textVariant('c3'),
    color: theme.baseColors.primarySolid700,
    fontWeight: '600',
  },
  reactionBadge: {
    ...theme.presets.row,
    ...theme.presets.alignCenter,
    alignSelf: 'flex-end',
    gap: theme.spacing.value(0.5),
    backgroundColor: theme.baseColors.neutralSolid200,
    borderRadius: theme.radius.small,
    padding: '2px 6px',
  },
  reactionBadgeIcon: {
    ...theme.sized(1),
    color: theme.baseColors.neutralSolid700,
  },
  reactionBadgeText: {
    ...textVariant('c3'),
    color: theme.baseColors.neutralSolid900,
    fontWeight: '600',
  },
  // Pre-computed description icon styles to avoid inline object creation
  descriptionIconRead: {
    color: theme.baseColors.primarySolid300,
  },
  descriptionIconUnread: {
    color: theme.baseColors.neutralSolid400,
  },
}))
